import { CheckOutlined } from "@ant-design/icons"
import React from "react"
import { useTranslation } from "react-i18next"

import { cn } from "../../../../utils/cn.ts"
import { scrollToAndFocus } from "../../helpers/scroll-focus"
import {
  BUILDER_SECTIONS,
  type BuilderSectionKey,
  sectionElementId,
  sectionHeadingId,
  sectionTitle
} from "../../helpers/section-completion"

const goToSection = (key: BuilderSectionKey) =>
  scrollToAndFocus(sectionElementId(key), sectionHeadingId(key))

export function BuilderProgress({
  completion
}: {
  completion: Record<BuilderSectionKey, boolean>
}) {
  const { t } = useTranslation()
  // Sized by its own width, not the viewport: beside the live review at
  // xl the column is too narrow for four full labels in a row.
  return (
    <nav
      aria-label={t("settingsAutomation.builder.progress.label")}
      className={"@container"}
    >
      <ol
        className={
          "m-0 grid list-none grid-cols-2 gap-2 p-0 @min-[680px]:grid-cols-4"
        }
      >
        {BUILDER_SECTIONS.map(({ key, number }) => {
          const complete = completion[key]
          const title = sectionTitle(t, key)
          return (
            <li key={key} className={"min-w-0"}>
              <button
                type={"button"}
                aria-label={t(
                  `settingsAutomation.builder.progress.${complete ? "stepComplete" : "stepIncomplete"}`,
                  { title }
                )}
                onClick={() => goToSection(key)}
                className={cn(
                  "flex min-h-11 w-full items-center gap-2 rounded-lg border bg-white px-3 py-2 text-start text-sm",
                  "outline-none focus-visible:ring-2 focus-visible:ring-primary",
                  complete
                    ? "border-primary"
                    : "border-gray-200 hover:border-gray-300"
                )}
              >
                <span
                  aria-hidden={"true"}
                  className={cn(
                    "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                    complete
                      ? "bg-primary text-white"
                      : "bg-gray-100 text-gray-600"
                  )}
                >
                  {complete ? <CheckOutlined /> : number}
                </span>
                <span className={"min-w-0 break-words font-medium"}>
                  {title}
                </span>
              </button>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
